// covered-by: frontends/carrie/src/utils/queries/__tests__/askPunttProjects.test.tsx
// covered-by: qa/rickrubin/tests/api/ask-puntt-projects.spec.ts
import {
  type AskPunttProjectScope,
  type GetAskPunttProjectResponse,
  type ListAskPunttProjectsResponse,
} from "@mg/schemas/src/christo/askPunttProject";
import { type ListParticipationTasksResponse } from "@mg/schemas/src/christo/participationTask";
import {
  type Query,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { askPunttKeys } from "./askPuntt";

import { useUI } from "../../contexts/ui";
import {
  type AppendProjectFeedPostRequest,
  type ListProjectTimelineResponse,
  type ProjectSuggestionResolution,
} from "../../services/askPunttProjectFeed.types";
import * as ProjectsService from "../../services/askPunttProjects";
import { collectUniqueProjectMembers } from "../projectListMembers";

/**
 * React Query hooks for the Ask Puntt Projects API. Mirrors the chat hooks in
 * ./askPuntt.tsx — broad prefix keys for invalidation, scope-aware lists.
 */
export const askPunttProjectKeys = {
  all: ["ask-puntt-projects"] as const,
  listsPrefix: ["ask-puntt-projects", "list"] as const,
  list: (filters?: {
    scope?: AskPunttProjectScope;
    search?: string;
    participantIds?: string[];
    regions?: string[];
    tags?: string[];
    sortBy?: "updatedAt" | "createdAt" | "name";
    includeReviewState?: boolean;
  }) =>
    [
      ...askPunttProjectKeys.all,
      "list",
      filters?.scope ?? "mine",
      filters?.search?.trim() ?? "",
      filters?.includeReviewState ? "with-review-state" : "no-review-state",
      filters?.sortBy ?? "updatedAt",
      ...(filters?.participantIds ?? []).slice().sort(),
      ...(filters?.regions ?? []).slice().sort(),
      ...(filters?.tags ?? []).slice().sort(),
    ] as const,
  memberOptions: () => [...askPunttProjectKeys.all, "member-options"] as const,
  project: (projectId: string) =>
    [...askPunttProjectKeys.all, "project", projectId] as const,
  feed: (projectId: string) =>
    [...askPunttProjectKeys.all, "feed", projectId] as const,
  timeline: (projectId: string) =>
    [...askPunttProjectKeys.all, "timeline", projectId] as const,
  reviews: (projectId: string) =>
    [...askPunttProjectKeys.all, "reviews", projectId] as const,
  participation: (projectId: string, versionId: string) =>
    [
      ...askPunttProjectKeys.all,
      "participation",
      projectId,
      versionId,
    ] as const,
  participationTasks: (filters?: {
    projectId?: string;
    status?: "open" | "cleared";
    kind?: "suggested" | "invited";
  }) =>
    [
      ...askPunttProjectKeys.all,
      "participation-tasks",
      filters?.projectId ?? "",
      filters?.status ?? "open",
      filters?.kind ?? "",
    ] as const,
} as const;

export type AskPunttProjectsListFilters = {
  scope?: AskPunttProjectScope;
  search?: string;
  participantIds?: string[];
  regions?: string[];
  tags?: string[];
  sortBy?: "updatedAt" | "createdAt" | "name";
  /** Opt-in to per-project coarse `reviewState` (extra backend hydration). */
  includeReviewState?: boolean;
};

/** List the requester's projects (scope + optional API search/participant filters). */
export function useAskPunttProjects(
  options?: {
    enabled?: boolean;
    refetchInterval?: number | false;
  } & AskPunttProjectsListFilters,
) {
  const scope = options?.scope ?? "mine";
  const search = options?.search?.trim() || undefined;
  const includeReviewState = options?.includeReviewState ?? false;
  const participantIds = Array.from(
    new Set(
      (options?.participantIds ?? [])
        .map((id) => id.trim())
        .filter((id) => id.length > 0),
    ),
  );
  const regions = options?.regions;
  const tags = options?.tags;
  const sortBy =
    options?.sortBy === "createdAt" ||
    options?.sortBy === "updatedAt" ||
    options?.sortBy === "name"
      ? options.sortBy
      : "updatedAt";
  return useQuery({
    queryKey: askPunttProjectKeys.list({
      scope,
      search,
      participantIds,
      regions,
      tags,
      sortBy: options?.sortBy ?? "updatedAt",
      includeReviewState,
    }),
    queryFn: () =>
      ProjectsService.listProjects({
        limit: 100,
        scope,
        search,
        participantIds: participantIds.length > 0 ? participantIds : undefined,
        regions,
        tags,
        sortBy,
        includeReviewState,
      }),
    enabled: options?.enabled !== false,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: options?.refetchInterval,
  });
}

/** Unfiltered project list used to populate the participant filter dropdown. */
export function useAskPunttProjectMemberOptions(options?: {
  enabled?: boolean;
}) {
  return useQuery({
    queryKey: askPunttProjectKeys.memberOptions(),
    queryFn: () => ProjectsService.listProjects({ limit: 100, scope: "all" }),
    enabled: options?.enabled !== false,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    select: (data) => collectUniqueProjectMembers(data.projects),
  });
}

/**
 * Fetch a single project with its chats (shared chat + the requester's own
 * personal chats) and full files array. Pass `refetchInterval` to poll while a
 * chat is being promoted or files change.
 */
export function useAskPunttProject(
  projectId: string | null,
  options?: { enabled?: boolean; refetchInterval?: number | false },
) {
  return useQuery({
    queryKey: askPunttProjectKeys.project(projectId || ""),
    queryFn: () => ProjectsService.getProject(projectId!),
    enabled: !!projectId && options?.enabled !== false,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: options?.refetchInterval,
  });
}

/** Create a project (server auto-creates its shared chat). */
export function useCreateAskPunttProject(
  createFn: typeof ProjectsService.createProject = ProjectsService.createProject,
) {
  const queryClient = useQueryClient();
  const { notify } = useUI();
  return useMutation({
    // why: TanStack Query v5 calls mutationFn(variables, context). The create
    // seam (rembrandt or warhol) only accepts the project payload.
    mutationFn: (payload: Parameters<typeof createFn>[0]) => createFn(payload),
    onSuccess: async () => {
      // why: invalidate only cancels an in-flight fetch that already has data;
      // a first list load sent before this POST is adopted as the "refetch" and
      // settles without the new project (home picker never shows it).
      await queryClient.cancelQueries({
        queryKey: askPunttProjectKeys.listsPrefix,
      });
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.listsPrefix,
      });
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.memberOptions(),
      });
    },
    onError: (error) => {
      console.error("Failed to create project:", error);
      notify({
        title: "Failed to create project",
        message: "Please try again.",
        variant: "error",
      });
    },
  });
}

/** Update project metadata (owner-only). */
export function useUpdateAskPunttProject(projectId: string) {
  const queryClient = useQueryClient();
  const { notify } = useUI();
  return useMutation({
    mutationFn: (payload: {
      name?: string;
      description?: string;
      context?: string;
      upcomingWork?: string;
    }) => ProjectsService.updateProject(projectId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.project(projectId),
      });
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.listsPrefix,
      });
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.memberOptions(),
      });
    },
    onError: (error) => {
      console.error("Failed to update project:", error);
      notify({
        title: "Failed to update project",
        message: "Please try again.",
        variant: "error",
      });
    },
  });
}

/**
 * Rename a project (owner-only). Mirrors `useRenameAskPunttChat`: optimistically
 * updates the project name in BOTH the single-project cache (drives the project
 * breadcrumb/header) AND every cached project list (drives the launcher +
 * sidebar), then invalidates so the server's canonical name wins. Reverts every
 * touched cache and toasts on error.
 */
export function useRenameAskPunttProject(projectId: string) {
  const queryClient = useQueryClient();
  const { notify } = useUI();
  return useMutation({
    mutationFn: (name: string) =>
      ProjectsService.updateProject(projectId, { name }),
    onMutate: async (name) => {
      const trimmed = name.trim();
      const projectKey = askPunttProjectKeys.project(projectId);
      await Promise.all([
        queryClient.cancelQueries({ queryKey: projectKey }),
        queryClient.cancelQueries({
          queryKey: askPunttProjectKeys.listsPrefix,
        }),
      ]);

      const previousProject =
        queryClient.getQueryData<GetAskPunttProjectResponse>(projectKey);
      const previousLists =
        queryClient.getQueriesData<ListAskPunttProjectsResponse>({
          queryKey: askPunttProjectKeys.listsPrefix,
        });

      queryClient.setQueryData<GetAskPunttProjectResponse>(projectKey, (old) =>
        old ? { ...old, project: { ...old.project, name: trimmed } } : old,
      );

      // Update every cached project list (scopes/filters) so launcher + sidebar
      // rows reflect the new name immediately.
      for (const [key, value] of previousLists) {
        if (!value?.projects) continue;
        queryClient.setQueryData<ListAskPunttProjectsResponse>(key, {
          ...value,
          projects: value.projects.map((p) =>
            p._id === projectId ? { ...p, name: trimmed } : p,
          ),
        });
      }

      return { previousProject, previousLists, projectKey };
    },
    onError: (error, _name, context) => {
      if (context?.previousProject !== undefined) {
        queryClient.setQueryData(context.projectKey, context.previousProject);
      }
      for (const [key, value] of context?.previousLists ?? []) {
        queryClient.setQueryData(key, value);
      }
      console.error("Failed to rename project:", error);
      notify({
        title: "Failed to rename project",
        message: "Your change was reverted. Please try again.",
        variant: "error",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.project(projectId),
      });
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.listsPrefix,
      });
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.memberOptions(),
      });
    },
  });
}

/**
 * Soft-delete a project (owner-only). Mirrors `useDeleteAskPunttChat`: on
 * success it invalidates the project lists + member options so the sidebar
 * and project launcher drop the deleted project, and removes the single
 * project query from cache. The backend cascade (soft-delete the shared chat,
 * detach members' personal chats) is handled server-side.
 */
export function useDeleteAskPunttProject() {
  const queryClient = useQueryClient();
  const { notify } = useUI();
  return useMutation({
    mutationFn: (projectId: string) => ProjectsService.deleteProject(projectId),
    onSuccess: (_data, projectId) => {
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.listsPrefix,
      });
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.memberOptions(),
      });
      queryClient.removeQueries({
        queryKey: askPunttProjectKeys.project(projectId),
      });
      queryClient.removeQueries({
        queryKey: askPunttProjectKeys.reviews(projectId),
      });
      queryClient.removeQueries({
        queryKey: askPunttProjectKeys.feed(projectId),
      });
      queryClient.removeQueries({
        queryKey: askPunttProjectKeys.timeline(projectId),
      });
      // Drop pending approvals for the deleted project from every
      // participation-task cache (home inbox + project-scoped lists).
      queryClient.setQueriesData<ListParticipationTasksResponse>(
        { queryKey: [...askPunttProjectKeys.all, "participation-tasks"] },
        (old) =>
          old
            ? { tasks: old.tasks.filter((t) => t.projectId !== projectId) }
            : old,
      );
      queryClient.invalidateQueries({
        queryKey: [...askPunttProjectKeys.all, "participation-tasks"],
      });
      // Detaching personal chats + soft-deleting the shared chat happens
      // server-side; refresh the chat lists so they reflect the teardown.
      queryClient.invalidateQueries({ queryKey: askPunttKeys.chatsPrefix });
    },
    onError: (error) => {
      console.error("Failed to delete project:", error);
      notify({
        title: "Failed to delete project",
        message: "Please try again.",
        variant: "error",
      });
    },
  });
}

/**
 * Add one or more members to a project (owner, or an enterprise coordinator
 * who can reach the project) — backs the header "+ Invite" modal. Invalidates
 * the project so the avatar stack + member count update in place.
 */
export function useAddProjectMembers(projectId: string) {
  const queryClient = useQueryClient();
  const { notify } = useUI();
  return useMutation({
    mutationFn: (payload: { userIds?: string[]; emails?: string[] }) =>
      ProjectsService.addProjectMembers(projectId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.project(projectId),
      });
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.feed(projectId),
      });
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.timeline(projectId),
      });
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.listsPrefix,
      });
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.memberOptions(),
      });
    },
    onError: (error) => {
      console.error("Failed to invite members:", error);
      notify({
        title: "Failed to invite",
        message: "Please try again.",
        variant: "error",
      });
    },
  });
}

/**
 * Read a project's feed (chronological, newest-first server-side). Pass
 * `refetchInterval` to poll while a review runs or an integration syncs so
 * the timeline + composer reflect new events without a manual refresh.
 */
export function useProjectFeed(
  projectId: string | null,
  options?: { enabled?: boolean; refetchInterval?: number | false },
) {
  return useQuery({
    queryKey: askPunttProjectKeys.feed(projectId || ""),
    queryFn: () => ProjectsService.listProjectFeed(projectId!, { limit: 100 }),
    enabled: !!projectId && options?.enabled !== false,
    staleTime: 15 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: options?.refetchInterval,
  });
}

/**
 * Unified project feed timeline (feed events + shared-chat turns). Pass
 * `refetchInterval` to poll while Puntt is generating a reply or a review runs.
 */
export function useProjectTimeline(
  projectId: string | null,
  options?: {
    enabled?: boolean;
    refetchInterval?:
      | number
      | false
      | ((
          query: Query<ListProjectTimelineResponse>,
        ) => number | false | undefined);
  },
) {
  return useQuery({
    queryKey: askPunttProjectKeys.timeline(projectId || ""),
    queryFn: () =>
      ProjectsService.listProjectTimeline(projectId!, { limit: 200 }),
    enabled: !!projectId && options?.enabled !== false,
    staleTime: 5 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: options?.refetchInterval,
  });
}

/** Append a user post to the project feed (composer submit). */
export function useAppendFeedPost(projectId: string) {
  const queryClient = useQueryClient();
  const { notify } = useUI();
  return useMutation({
    mutationFn: (payload: AppendProjectFeedPostRequest) =>
      ProjectsService.appendProjectFeedPost(projectId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.feed(projectId),
      });
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.timeline(projectId),
      });
    },
    onError: (error) => {
      console.error("Failed to post to project feed:", error);
      notify({
        title: "Failed to post",
        message: "Please try again.",
        variant: "error",
      });
    },
  });
}

/**
 * Share one of the requester's personal project chats with the whole project
 * (the "Share to project" action). On success refetches the project (so the
 * chat now renders as shared) and the feed (the share note appears).
 */
export function useShareProjectChat(projectId: string) {
  const queryClient = useQueryClient();
  const { notify } = useUI();
  return useMutation({
    mutationFn: (chatId: string) =>
      ProjectsService.shareProjectChat(projectId, chatId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.project(projectId),
      });
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.feed(projectId),
      });
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.timeline(projectId),
      });
      notify({
        title: "Shared to project",
        message: "Everyone on the project can now see this chat.",
        variant: "success",
      });
    },
    onError: (error) => {
      console.error("Failed to share chat to project:", error);
      notify({
        title: "Failed to share chat",
        message: "Please try again.",
        variant: "error",
      });
    },
  });
}

/**
 * Read the active reviews for a project (right-hand Reviews panel). Pass
 * `refetchInterval` to keep the IN REVIEW chip + version cards live while a
 * re-review runs after a new version upload.
 */
export function useProjectReviews(
  projectId: string | null,
  options?: { enabled?: boolean; refetchInterval?: number | false },
) {
  return useQuery({
    queryKey: askPunttProjectKeys.reviews(projectId || ""),
    queryFn: () => ProjectsService.getProjectReviews(projectId!),
    enabled: !!projectId && options?.enabled !== false,
    staleTime: 15 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: options?.refetchInterval,
  });
}

/** Participation snapshot + events for one file version. */
export function useApprovalParticipation(
  projectId: string | null,
  versionId: string | null,
  fileId?: string | null,
  options?: { enabled?: boolean },
) {
  return useQuery({
    queryKey: askPunttProjectKeys.participation(
      projectId || "",
      versionId || "",
    ),
    queryFn: () =>
      ProjectsService.getApprovalParticipation(
        projectId!,
        versionId!,
        fileId || undefined,
      ),
    enabled: !!projectId && !!versionId && options?.enabled !== false,
    staleTime: 15 * 1000,
    gcTime: 5 * 60 * 1000,
  });
}

function invalidateParticipation(
  queryClient: ReturnType<typeof useQueryClient>,
  projectId: string,
  versionId: string,
) {
  queryClient.invalidateQueries({
    queryKey: askPunttProjectKeys.participation(projectId, versionId),
  });
  queryClient.invalidateQueries({
    queryKey: askPunttProjectKeys.reviews(projectId),
  });
  queryClient.invalidateQueries({
    queryKey: askPunttProjectKeys.project(projectId),
  });
  queryClient.invalidateQueries({
    queryKey: askPunttProjectKeys.participationTasks({ projectId }),
  });
  // Home inbox lists open tasks across all projects (no projectId filter).
  queryClient.invalidateQueries({
    queryKey: [...askPunttProjectKeys.all, "participation-tasks"],
  });
  queryClient.invalidateQueries({
    queryKey: askPunttProjectKeys.feed(projectId),
  });
  queryClient.invalidateQueries({
    queryKey: askPunttProjectKeys.timeline(projectId),
  });
}

/** Open participation tasks for the current user (optionally scoped to a project). */
export function useParticipationTasks(options?: {
  projectId?: string | null;
  status?: "open" | "cleared";
  kind?: "suggested" | "invited";
  limit?: number;
  enabled?: boolean;
  refetchInterval?: number | false;
}) {
  const projectId = options?.projectId ?? undefined;
  const status = options?.status ?? "open";
  const kind = options?.kind;
  return useQuery({
    queryKey: askPunttProjectKeys.participationTasks({
      projectId,
      status,
      kind,
    }),
    queryFn: () =>
      ProjectsService.listParticipationTasks({
        projectId,
        status,
        kind,
        limit: options?.limit,
      }),
    enabled: options?.enabled !== false,
    staleTime: 15 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: options?.refetchInterval,
  });
}

/**
 * Record approve / changes_requested. Prefer
 * {@link useRecordProjectPosition} when versionId varies per submit.
 */
export function useRecordPosition(projectId: string, versionId: string) {
  const mutation = useRecordProjectPosition(projectId);
  return {
    ...mutation,
    mutate: (
      input: {
        fileId: string;
        position: "approved" | "changes_requested";
        noteBody?: string;
        ticketId?: string | null;
        idempotencyKey: string;
        versionId?: string;
      },
      opts?: Parameters<typeof mutation.mutate>[1],
    ) =>
      mutation.mutate(
        { ...input, versionId: input.versionId ?? versionId },
        opts,
      ),
    mutateAsync: (
      input: {
        fileId: string;
        position: "approved" | "changes_requested";
        noteBody?: string;
        ticketId?: string | null;
        idempotencyKey: string;
        versionId?: string;
      },
      opts?: Parameters<typeof mutation.mutateAsync>[1],
    ) =>
      mutation.mutateAsync(
        { ...input, versionId: input.versionId ?? versionId },
        opts,
      ),
  };
}

/** Project-scoped position write — versionId is required on each mutate. */
export function useRecordProjectPosition(projectId: string) {
  const queryClient = useQueryClient();
  const { notify } = useUI();
  return useMutation({
    mutationFn: (input: {
      versionId: string;
      fileId: string;
      position: "approved" | "changes_requested";
      noteBody?: string;
      ticketId?: string | null;
      idempotencyKey: string;
    }) =>
      ProjectsService.appendApprovalEvent(projectId, input.versionId, {
        type: "position_recorded",
        fileId: input.fileId,
        versionId: input.versionId,
        ticketId: input.ticketId,
        position: input.position,
        noteBody: input.noteBody,
        idempotencyKey: input.idempotencyKey,
        source: "app",
      }),
    onSuccess: (_data, vars) => {
      invalidateParticipation(queryClient, projectId, vars.versionId);
    },
    onError: (error) => {
      console.error("Failed to record position:", error);
      notify({
        title: "Couldn't record your decision",
        message: "Please try again.",
        variant: "error",
      });
    },
  });
}

/** Project-scoped standalone approval note — versionId required on each mutate. */
export function useAddProjectApprovalNote(projectId: string) {
  const queryClient = useQueryClient();
  const { notify } = useUI();
  return useMutation({
    mutationFn: (input: {
      versionId: string;
      fileId: string;
      noteBody: string;
      parentNoteId?: string | null;
      ticketId?: string | null;
      idempotencyKey: string;
    }) =>
      ProjectsService.appendApprovalEvent(projectId, input.versionId, {
        type: "note_added",
        fileId: input.fileId,
        versionId: input.versionId,
        ticketId: input.ticketId,
        noteBody: input.noteBody,
        parentNoteId: input.parentNoteId,
        idempotencyKey: input.idempotencyKey,
        source: "app",
      }),
    onSuccess: (_data, vars) => {
      invalidateParticipation(queryClient, projectId, vars.versionId);
    },
    onError: (error) => {
      console.error("Failed to add approval note:", error);
      notify({
        title: "Couldn't add note",
        message: "Please try again.",
        variant: "error",
      });
    },
  });
}

/** Leave a standalone approval note (no verdict). */
export function useAddApprovalNote(projectId: string, versionId: string) {
  const mutation = useAddProjectApprovalNote(projectId);
  return {
    ...mutation,
    mutate: (
      input: {
        fileId: string;
        noteBody: string;
        parentNoteId?: string | null;
        ticketId?: string | null;
        idempotencyKey: string;
        versionId?: string;
      },
      opts?: Parameters<typeof mutation.mutate>[1],
    ) =>
      mutation.mutate(
        { ...input, versionId: input.versionId ?? versionId },
        opts,
      ),
    mutateAsync: (
      input: {
        fileId: string;
        noteBody: string;
        parentNoteId?: string | null;
        ticketId?: string | null;
        idempotencyKey: string;
        versionId?: string;
      },
      opts?: Parameters<typeof mutation.mutateAsync>[1],
    ) =>
      mutation.mutateAsync(
        { ...input, versionId: input.versionId ?? versionId },
        opts,
      ),
  };
}

/** Invite a suggested (or any) member to weigh in — optional, not a blocker. */
export function useInviteParticipant(projectId: string, versionId: string) {
  const queryClient = useQueryClient();
  const { notify } = useUI();
  return useMutation({
    mutationFn: (input: {
      fileId: string;
      subjectUserId: string;
      suggestedRole?: string | null;
      ticketId?: string | null;
      idempotencyKey: string;
    }) =>
      ProjectsService.appendApprovalEvent(projectId, versionId, {
        type: "participant_invited",
        fileId: input.fileId,
        versionId,
        ticketId: input.ticketId,
        subjectUserId: input.subjectUserId,
        suggestedRole: input.suggestedRole,
        idempotencyKey: input.idempotencyKey,
        source: "app",
      }),
    onSuccess: () => {
      invalidateParticipation(queryClient, projectId, versionId);
    },
    onError: (error) => {
      console.error("Failed to invite participant:", error);
      notify({
        title: "Couldn't send invite",
        message: "Please try again.",
        variant: "error",
      });
    },
  });
}

/** Decline an invitation for the current member ("not needed from me"). */
export function useDeclineInvitation(projectId: string, versionId: string) {
  const mutation = useDeclineProjectInvitation(projectId);
  return {
    ...mutation,
    mutate: (
      input: {
        fileId: string;
        ticketId?: string | null;
        idempotencyKey: string;
        versionId?: string;
      },
      opts?: Parameters<typeof mutation.mutate>[1],
    ) =>
      mutation.mutate(
        { ...input, versionId: input.versionId ?? versionId },
        opts,
      ),
    mutateAsync: (
      input: {
        fileId: string;
        ticketId?: string | null;
        idempotencyKey: string;
        versionId?: string;
      },
      opts?: Parameters<typeof mutation.mutateAsync>[1],
    ) =>
      mutation.mutateAsync(
        { ...input, versionId: input.versionId ?? versionId },
        opts,
      ),
  };
}

/** Project-scoped decline — versionId is required on each mutate. */
export function useDeclineProjectInvitation(projectId: string) {
  const queryClient = useQueryClient();
  const { notify } = useUI();
  return useMutation({
    mutationFn: (input: {
      versionId: string;
      fileId: string;
      ticketId?: string | null;
      idempotencyKey: string;
    }) =>
      ProjectsService.appendApprovalEvent(projectId, input.versionId, {
        type: "invitation_declined",
        fileId: input.fileId,
        versionId: input.versionId,
        ticketId: input.ticketId,
        idempotencyKey: input.idempotencyKey,
        source: "app",
      }),
    onSuccess: (_data, vars) => {
      invalidateParticipation(queryClient, projectId, vars.versionId);
    },
    onError: (error) => {
      console.error("Failed to decline invitation:", error);
      notify({
        title: "Couldn't update invitation",
        message: "Please try again.",
        variant: "error",
      });
    },
  });
}

/**
 * Resolve a feed suggestion (ignore | approve | adjust). Refetches the feed
 * (the resolved suggestion flips to resolved server-side) and, on approve,
 * the reviews panel + project (an approve may invite a member or re-review).
 */
export function useResolveFeedSuggestion(projectId: string) {
  const queryClient = useQueryClient();
  const { notify } = useUI();
  return useMutation({
    mutationFn: ({
      eventId,
      action,
      payload,
    }: {
      eventId: string;
      action: ProjectSuggestionResolution;
      payload?: Record<string, unknown>;
    }) =>
      ProjectsService.resolveProjectSuggestion(projectId, eventId, {
        action,
        payload,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.feed(projectId),
      });
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.timeline(projectId),
      });
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.reviews(projectId),
      });
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.project(projectId),
      });
    },
    onError: (error) => {
      console.error("Failed to resolve suggestion:", error);
      notify({
        title: "Couldn't update suggestion",
        message: "Please try again.",
        variant: "error",
      });
    },
  });
}

/**
 * Promote a standalone chat into a new project (idempotent). On success the
 * project lists + the source chat are invalidated so the new project shows
 * up in the sidebar and the chat reflects its new `projectId`.
 */
export function usePromoteChatToProject() {
  const queryClient = useQueryClient();
  const { notify } = useUI();
  return useMutation({
    mutationFn: (chatId: string) =>
      ProjectsService.promoteChatToProject(chatId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.listsPrefix,
      });
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.memberOptions(),
      });
      queryClient.invalidateQueries({ queryKey: askPunttKeys.chatsPrefix });
    },
    onError: (error) => {
      console.error("Failed to promote chat to project:", error);
      notify({
        title: "Couldn't create project",
        message: "Please try again.",
        variant: "error",
      });
    },
  });
}

/**
 * Promote a personal chat into the project (owner-only). On success the chat
 * becomes the project's shared scope and the team is notified server-side.
 */
export function useAddChatToProject(projectId: string) {
  const queryClient = useQueryClient();
  const { notify } = useUI();
  return useMutation({
    mutationFn: (chatId: string) =>
      ProjectsService.addChatToProject(chatId, projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: askPunttProjectKeys.project(projectId),
      });
      queryClient.invalidateQueries({ queryKey: askPunttKeys.chatsPrefix });
    },
    onError: (error) => {
      console.error("Failed to share chat to project:", error);
      notify({
        title: "Failed to share chat",
        message: "Please try again.",
        variant: "error",
      });
    },
  });
}
